import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { logger } from '@/lib/logger'

/**
 * Utility function to merge Tailwind CSS classes
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Helper function to format file size
export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format time consistently for both server and client rendering
 * to avoid hydration mismatches
 */
export function formatTimeConsistent(timestamp: string): string {
  try {
    const date = new Date(timestamp);

    // Use consistent 24-hour format to avoid locale differences
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  } catch (error) {
    logger.error('Error formatting timestamp', error);
    return '';
  }
}

/**
 * Sanitizes the input text by removing specific placeholders.
 * @param text - The text to sanitize
 * @returns The sanitized text
 */
export function sanitizeText(text: string) {
  let result = text.replace('<has_function_call>', '');

  // Strip complete(text="...") / ask(text="...") wrappers from legacy messages
  // where models generated tool syntax as plain text instead of actual tool calls
  const wrapperMatch = result.match(/^(?:complete|ask)\(text="([\s\S]*)"\)$/);
  if (wrapperMatch) {
    result = wrapperMatch[1]
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }

  return result;
}